<ng-template #travellerTabs>
  <p-tabs [value]="selectedTravellerId()" (valueChange)="selectTraveller($any($event))">
    <div class="bg-white">
      <p-tablist>
        @for (traveller of sortedTravellers(); track traveller.travellerId) {
          <!-- Disabled while saving: changing tab reloads the itinerary, which is not queued behind a save. -->
          <p-tab [value]="traveller.travellerId ?? 0" [disabled]="!isReadOnly() && store.isSavingData()">
            @if (traveller.travellerId === primaryTravellerId()) {
              <i class="fa fa-star text-yellow-500"></i>
            } @else if (isLinkedToPrimary(traveller.travellerId)) {
              <i class="fa fa-link"></i>
            } @else {
              <i class="fa fa-link-slash"></i>
            }
            <span class="pl-1">{{ traveller.details?.firstName + " " + traveller.details?.surname }}</span>
          </p-tab>
        }
      </p-tablist>
    </div>
  </p-tabs>
</ng-template>

@if (isReadOnly()) {
  @if (!!itinerary()) {
    <div class="w-full h-full">
      <ng-container [ngTemplateOutlet]="travellerTabs"></ng-container>
      <div class="flex flex-col gap-2 pt-4">
        <p-table [value]="rows()" [tableStyle]="{ 'min-width': '60rem' }" size="small" class="border-[1px] border-surface-400 rounded table-auto w-fit max-w-full">
          <ng-template #header>
            <tr class="text-surface-800 text-xs font-semibold font-['Roboto_Condensed']">
              <th class="rounded-tl" pTooltip="Personal Days">PD</th>
              <th>Day</th>
              <th class="grow">Location</th>
              <th>Start</th>
              <th>Location</th>
              <th>End</th>
              <th>Work Type</th>
              <th>Mode of Travel</th>
              <th>Supporting Info</th>
              <th>Accommodation</th>
              <th>Meals to be Claimed</th>
              <th class="rounded-tr">Catering</th>
            </tr>
          </ng-template>
          <ng-template #body let-row let-rowIndex="rowIndex">
            <tr class="text-surface-800 text-xs font-['Roboto_Condensed']">
              <td [ngClass]="{'rounded-bl' : (rowIndex === rows().length - 1)}">
                <p-check-box disabled [binary]="true" [ngModel]="row.isPersonalDay"></p-check-box>
              </td>
              <td>Day {{ row.dayNumber }}</td>
              <td class="bg-primary-50">{{ row.startLocation?.name }}</td>
              <td class="bg-primary-50">{{ row.start | date:'HH:mm' }}</td>
              <td class="bg-green-50">{{ row.endLocation?.name }}</td>
              <td class="bg-green-50">{{ row.end | date:'HH:mm' }}</td>
              <td>{{ row.workType }}</td>
              <td>{{ row.travelMode }}</td>
              <td>{{ row.supportingInfo }}</td>
              <td>{{ row.accommodationType }}</td>
              <td>
                <div class="flex gap-2">
                  <div class="flex items-center gap-1">
                    <p-check-box disabled [binary]="true" [ngModel]="row.mealClaims?.['breakfast']"></p-check-box>
                    <label>B'fast</label>
                  </div>
                  <div class="flex items-center gap-1">
                    <p-check-box disabled [binary]="true" [ngModel]="row.mealClaims?.['lunch']"></p-check-box>
                    <label>Lunch</label>
                  </div>
                  <div class="flex items-center gap-1">
                    <p-check-box disabled [binary]="true" [ngModel]="row.mealClaims?.['dinner']"></p-check-box>
                    <label>Dinner</label>
                  </div>
                </div>
              </td>
              <td [ngClass]="{'rounded-br' : (rowIndex === rows().length - 1)}">
                @if (row.isOverseas) {
                  <div class="flex items-center gap-1">
                    <p-check-box disabled [binary]="true" [ngModel]="row.mealClaims?.['overseas']"></p-check-box>
                    <label>Meals &amp; Incidentals</label>
                  </div>
                } @else {
                  <div class="flex gap-2">
                    <div class="flex items-center gap-1">
                      <p-radiobutton disabled [name]="'catered-ro-' + row.id" [value]="'Catered'" [ngModel]="row.cateringOption"></p-radiobutton>
                      <label>CLC Catered</label>
                    </div>
                    <div class="flex items-center gap-1">
                      <p-radiobutton disabled [name]="'catered-ro-' + row.id" [value]="'NotCatered'" [ngModel]="row.cateringOption"></p-radiobutton>
                      <label>Not Catered</label>
                    </div>
                  </div>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
        @if (!!itinerary()?.notes) {
          <div class="text-surface-700 text-xs font-medium font-['Inter'] leading-4 pt-2">Itinerary Notes</div>
          <div class="px-3 py-2 bg-surface-200 rounded-md w-2/3 text-sm">{{ itinerary()?.notes ?? '' }}</div>
        }
      </div>
    </div>
  } @else {
    <div class="w-full h-full">
      <div class="text-xs font-normal font-['Inter'] leading-[9.75px] px-4 py-2">
        <i>No itinerary.</i>
      </div>
    </div>
  }
} @else {
  <div class="w-full h-full flex flex-col gap-6">
    <p-card>
      <div class="flex flex-col gap-2">
        <div class="flex w-full bg-surface-0 p-6 gap-6 rounded-t-2xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)]">
          <div class="flex flex-col gap-3">
            <div class="self-stretch justify-start text-surface-800 text-base font-medium font-['Inter'] leading-6">Traveller Itineraries</div>
            <div class="self-stretch justify-start text-surface-600 text-sm font-light font-['Inter']">Complete the primary traveller’s itinerary for each day of the trip. The number of days sets the trip's duration.</div>
            <div class="self-stretch justify-start text-surface-600 text-sm font-light font-['Inter']">Travellers added after the primary traveller's itinerary is started share it (<i class="fa fa-link"></i>). Changes to a shared itinerary apply to everyone on it.</div>
          </div>
        </div>
        @if (sortedTravellers().length === 0) {
          <div class="flex flex-col gap-2 w-full bg-surface-50 p-3 rounded-b-2xl text-surface-500 text-sm font-['Inter']">
            <i>No travellers selected in Step 1.</i>
          </div>
        } @else if (!!itinerary()) {
          <div class="flex flex-col gap-2 w-full bg-surface-50 p-3 rounded-b-2xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)]">
            <ng-container [ngTemplateOutlet]="travellerTabs"></ng-container>
            <div class="flex flex-col gap-4">
              <p-table [value]="rows()"
                       [rowTrackBy]="trackItineraryItem"
                       class="border-[1px] border-surface-200 rounded-lg">
                <ng-template #header>
                  <tr class="text-surface-800 text-xs font-semibold font-['Roboto_Condensed']">
                    <th class="rounded-tl-lg" pTooltip="Personal Days">PD</th>
                    <th>Day</th>
                    <th>Location</th>
                    <th>Start</th>
                    <th>Location</th>
                    <th>End</th>
                    <th>Mode of Travel</th>
                    <th>Supporting Info</th>
                    <th>Accommodation</th>
                    <th>Meal Claims</th>
                    <th>Catering</th>
                    <th class="rounded-tr-lg w-20">Remove Days</th>
                  </tr>
                </ng-template>
                <ng-template #body let-row>
                  <tr class="text-surface-800 text-xs font-['Roboto_Condensed']" data-testid="template-itinerary-row">
                    <td style="padding-left: 12px; padding-right: 0;">
                      <p-check-box [binary]="true"
                                   [ngModel]="row.isPersonalDay"
                                   (ngModelChange)="updateItineraryItemField(row, 'isPersonalDay', $event)"
                      ></p-check-box>
                    </td>
                    <td class="min-w-24 w-24" style="padding-left: 8px; padding-right: 8px;">
                      <div class="pl-3 pr-1.5 py-1.5 rounded-md bg-surface-200 text-surface-500 whitespace-nowrap flex justify-between items-center border-surface-300 border-1 h-[31px]"
                           data-testid="template-itinerary-day">Day {{ row.dayNumber }}
                        <i class="fa-regular fa-calendar"></i>
                      </div>
                    </td>
                    <td class="bg-primary-50 min-w-36" style="padding-left: 8px; padding-right: 0;">
                      <p-autocomplete panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                      class="w-full"
                                      [ngModel]="row.startLocation"
                                      [dropdown]="true"
                                      [suggestions]="filteredLocations()"
                                      (completeMethod)="filterLocations($event)"
                                      placeholder="Type and/or select..."
                                      size="small"
                                      appendTo="body"
                                      optionLabel="name"
                                      [invalid]="!row.startLocation"
                                      (onSelect)="updateItineraryItemField(row, 'startLocationId', $event)"
                                      (onFocus)="selectLocationText($event)"
                      >
                        <ng-template #item let-location let-i="index">
                          <span [class.location-recent-last]="i === recentLocationCount() - 1">{{ location.name }}</span>
                        </ng-template>
                      </p-autocomplete>
                    </td>
                    <td class="bg-primary-50 min-w-26 w-26" style="padding-left: 8px; padding-right: 8px;">
                      <p-datepicker #datePickerStart
                                    panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                    [ngModel]="row.startDate"
                                    [timeOnly]="true"
                                    [showIcon]="true"
                                    [stepMinute]="15"
                                    [keepInvalid]="true"
                                    iconDisplay="input"
                                    (ngModelChange)="stageTime(row, 'start')"
                                    (onClose)="commitTime(row, 'start', datePickerStart)"
                                    (onBlur)="commitTime(row, 'start', datePickerStart)"
                                    appendTo="body"
                                    size="small"
                      >
                        <ng-template pTemplate="inputicon">
                          <div class="pt-0.5" (mousedown)="$event.preventDefault(); datePickerStart.toggle()">
                            <i class="fa-regular fa-lg fa-clock"></i>
                          </div>
                        </ng-template>
                      </p-datepicker>
                    </td>
                    <td class="bg-green-50 min-w-36" style="padding-left: 8px; padding-right: 0;">
                      <p-autocomplete panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                      class="w-full"
                                      [ngModel]="row.endLocation"
                                      [dropdown]="true"
                                      [suggestions]="filteredLocations()"
                                      (completeMethod)="filterLocations($event)"
                                      placeholder="Type and/or select..."
                                      size="small"
                                      appendTo="body"
                                      optionLabel="name"
                                      [invalid]="!row.endLocation"
                                      (onSelect)="updateItineraryItemField(row, 'endLocationId', $event)"
                                      (onFocus)="selectLocationText($event)"
                      >
                        <ng-template #item let-location let-i="index">
                          <span [class.location-recent-last]="i === recentLocationCount() - 1">{{ location.name }}</span>
                        </ng-template>
                      </p-autocomplete>
                    </td>
                    <td class="bg-green-50 min-w-26 w-26" style="padding-left: 8px; padding-right: 8px;">
                      <p-datepicker #datePickerEnd
                                    panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                    [ngModel]="row.endDate"
                                    [timeOnly]="true"
                                    [showIcon]="true"
                                    [stepMinute]="15"
                                    [keepInvalid]="true"
                                    iconDisplay="input"
                                    (ngModelChange)="stageTime(row, 'end')"
                                    (onClose)="commitTime(row, 'end', datePickerEnd)"
                                    (onBlur)="commitTime(row, 'end', datePickerEnd)"
                                    [minDate]="row.startDate"
                                    appendTo="body"
                                    size="small"
                      >
                        <ng-template pTemplate="inputicon">
                          <div class="pt-0.5" (mousedown)="$event.preventDefault(); datePickerEnd.toggle()">
                            <i class="fa-regular fa-lg fa-clock"></i>
                          </div>
                        </ng-template>
                      </p-datepicker>
                    </td>
                    <td class="max-w-28" style="padding-left: 8px; padding-right: 0;">
                      <p-select panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                [options]="availableModesOfTravel"
                                [ngModel]="row.travelMode"
                                (onChange)="updateItineraryItemField(row, 'travelMode', $event)"
                                placeholder="Select..."
                                size="small"
                                [showClear]="true"
                                [disabled]="!row.start || !row.workType || row.isPersonalDay"
                                class="w-full"
                                appendTo="body"/>
                    </td>
                    <td class="max-w-20" style="padding-left: 8px; padding-right: 0;">
                      <input class="text-surface-800 text-xs font-['Roboto_Condensed'] max-w-full"
                             type="text"
                             pInputText
                             [ngModel]="row.supportingInfo"
                             (blur)="updateItineraryItemField(row, 'supportingInfo', $event)"
                             [placeholder]="row.supportingInfoLabel"
                             pSize="small"
                             [disabled]="!row.start || !row.workType || row.isPersonalDay"
                      />
                    </td>
                    <td class="max-w-28" style="padding-left: 8px; padding-right: 0;">
                      @if (row.workType === 'Field') {
                        <p-select panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                  class="w-full"
                                  [options]="[{value: 'Camping', label: 'Camping'},{value: 'Other', label: 'Other'}]"
                                  [ngModel]="row.accommodationType"
                                  (onChange)="updateItineraryItemField(row, 'accommodationType', $event)"
                                  placeholder="Select..."
                                  size="small"
                                  [showClear]="true"
                                  [disabled]="!row.start || !row.workType || row.isPersonalDay"
                                  appendTo="body"/>
                      } @else if (row.workType === 'Other') {
                        <p-select panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                  class="w-full"
                                  [options]="[{value: 'Motel', label: 'Motel'},{value: 'Private', label: 'Private'}]"
                                  [ngModel]="row.accommodationType"
                                  (onChange)="updateItineraryItemField(row, 'accommodationType', $event)"
                                  placeholder="Select..."
                                  size="small"
                                  [showClear]="true"
                                  [disabled]="!row.start || !row.workType || row.isPersonalDay"
                                  appendTo="body"/>
                      } @else {
                        <p-select panelStyleClass="text-surface-800 text-xs font-['Roboto_Condensed']"
                                  class="w-full"
                                  disabled
                                  placeholder="Select..."
                                  size="small"
                                  appendTo="body"/>
                      }
                    </td>
                    <td style="padding-left: 8px; padding-right: 0;">
                      <div class="flex gap-2">
                        <div class="flex items-center gap-1">
                          <label>B'fast</label>
                          <p-check-box [binary]="true"
                                       [ngModel]="row.mealClaims?.['breakfast']"
                                       (onChange)="updateItineraryItemField(row, 'mealClaims', {'breakfast': $event.checked})"
                                       [disabled]="!row.start || !row.allowedFields.includes(MealTypes.Breakfast)"
                          ></p-check-box>
                        </div>
                        <div class="flex items-center gap-1">
                          <label>Lunch</label>
                          <p-check-box [binary]="true"
                                       [ngModel]="row.mealClaims?.['lunch']"
                                       (onChange)="updateItineraryItemField(row, 'mealClaims', {'lunch': $event.checked})"
                                       [disabled]="!row.start || !row.allowedFields.includes(MealTypes.Lunch)"
                          ></p-check-box>
                        </div>
                        <div class="flex items-center gap-1">
                          <label>Dinner</label>
                          <p-check-box [binary]="true"
                                       [ngModel]="row.mealClaims?.['dinner']"
                                       (onChange)="updateItineraryItemField(row, 'mealClaims', {'dinner': $event.checked})"
                                       [disabled]="!row.start || !row.allowedFields.includes(MealTypes.Dinner)"
                          ></p-check-box>
                        </div>
                      </div>
                    </td>
                    <td style="padding-left: 8px; padding-right: 8px;">
                      @if (row.isOverseas) {
                        <!-- Overseas days: one ATO meals & incidentals claim in place of catering -->
                        <div class="flex gap-2 justify-center">
                          <div class="flex items-center gap-1">
                            <label>Meals &amp; Incidentals</label>
                            <p-check-box [binary]="true"
                                         [ngModel]="row.mealClaims?.['overseas']"
                                         (onChange)="updateItineraryItemField(row, 'mealClaims', {'overseas': $event.checked})"
                                         [disabled]="!row.start || row.isPersonalDay || !row.allowedFields.includes(MealTypes.Overseas)"
                            ></p-check-box>
                          </div>
                        </div>
                      } @else {
                        <div class="flex gap-2">
                          <div class="flex items-center gap-1 ml-1">
                            <label>CLC Catered</label>
                            <p-radiobutton class="ml-1" [name]="'catered-' + row.id"
                                           [value]="'Catered'"
                                           [ngModel]="row.cateringOption"
                                           (onClick)="updateItineraryItemField(row, 'cateringOption', $event)"
                                           [disabled]="!row.start || !row.allowedFields.includes(MealTypes.Camping)"
                            ></p-radiobutton>
                          </div>
                          <div class="flex items-center gap-1 ml-1">
                            <label>Not Catered</label>
                            <p-radiobutton class="ml-1" [name]="'catered-' + row.id"
                                           [value]="'NotCatered'"
                                           [ngModel]="row.cateringOption"
                                           (onClick)="updateItineraryItemField(row, 'cateringOption', $event)"
                                           [disabled]="!row.start || !row.allowedFields.includes(MealTypes.Camping)"
                            ></p-radiobutton>
                          </div>
                        </div>
                      }
                    </td>
                    <td class="bg-surface-50">
                      <div class="flex justify-center gap-1">
                        <icon-btn variant="minus-red"
                                  ariaLabel="Remove day"
                                  data-testid="template-itinerary-remove-day"
                                  [disabled]="rows().length <= 1"
                                  (click)="removeDay(row)" />
                      </div>
                    </td>
                  </tr>
                </ng-template>
                <ng-template #footer>
                  <!-- `!`: PrimeNG's unlayered tfoot cell background otherwise beats Tailwind's bg utilities. -->
                  <tr class="text-surface-800 text-xs font-normal font-['Roboto_Condensed']">
                    <td colspan="2" class="rounded-bl-lg"></td>
                    <td colspan="2" class="bg-primary-50!"></td>
                    <td colspan="2" class="bg-green-50!"></td>
                    <td colspan="5"></td>
                    <td class="bg-surface-50! rounded-br-lg">
                      <div class="flex items-center gap-1 cursor-pointer whitespace-nowrap bg-surface-50"
                           data-testid="template-itinerary-add-day"
                           (click)="addDay()">
                        <icon-btn variant="add-primary" ariaLabel="Add a day" />
                        <span>Add a Day</span>
                      </div>
                    </td>
                  </tr>
                </ng-template>
              </p-table>

              <p-floatlabel variant="in">
                <textarea pTextarea
                          id="in_label_template_itinerary_notes"
                          [ngModel]="workingNotes()"
                          (ngModelChange)="onNotesChange($event)"
                          (blur)="onNotesBlur()"
                          placeholder="Provide any additional notes to support your proposed itinerary."
                          rows="3"
                          pSize="small"
                          [autoResize]="true"
                          class="lg:w-1/2 w-full"
                ></textarea>
                <label for="in_label_template_itinerary_notes">Itinerary Notes</label>
              </p-floatlabel>
            </div>
          </div>
        } @else {
          <div class="flex flex-col gap-2 w-full bg-surface-50 p-3 rounded-b-2xl text-surface-500 text-sm font-['Inter']">
            <i>{{ templateDatasource.isLoadingItinerary() ? 'Loading itinerary...' : 'No itinerary.' }}</i>
          </div>
        }
      </div>
    </p-card>
  </div>
}
